import { Image as I, ImageProps as IP, ImageSource } from "expo-image";
import {
  fetchQuery,
  graphql,
  useFragment,
  useRelayEnvironment,
} from "react-relay";
import { ImageRefetchQuery } from "./__generated__/ImageRefetchQuery.graphql";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { ImageFinishUploadSubscription } from "./__generated__/ImageFinishUploadSubscription.graphql";
import { Image_image$key } from "./__generated__/Image_image.graphql";
import { Platform, Text } from "react-native";

import {
  GraphQLSubscriptionConfig,
  requestSubscription,
  OperationType,
} from "relay-runtime";
import { ImagePublic_image$key } from "./__generated__/ImagePublic_image.graphql";
import { useUploadedImages } from "../common";
import { ImagePublicOrPrivate_image$key } from "./__generated__/ImagePublicOrPrivate_image.graphql";
import { Upload } from "../../../mobile/src/OS/ImageUploadProvider";
import { AHText } from "./Text";

type Options = { skip: boolean };
export function useSubscriptionWithSkip<
  TSubscriptionPayload extends OperationType = OperationType,
>(
  config: GraphQLSubscriptionConfig<TSubscriptionPayload>,
  opts: Options = { skip: false }
): void {
  const environment = useRelayEnvironment();

  const skip = opts.skip;
  useEffect(() => {
    if (skip) {
      return;
    }
    const { dispose } = requestSubscription(environment, config);
    return dispose;
  }, [environment, config, skip]);
}

export type RemoteImageSource = {
  image: Image_image$key & { id: string };

  uploadedImages?: Upload[];
};
export type RemoteImageProps = Omit<IP, "source"> & RemoteImageSource;
export type RemotePublicImageProps = Omit<IP, "source"> & {
  image: ImagePublic_image$key;
};

const defaultTransition =
  Platform.OS === "ios"
    ? {
        duration: 200,
        effect: "cross-dissolve" as const,
      }
    : null;
export function RemotePublicImage(_props: RemotePublicImageProps) {
  const { image, ...props } = _props;
  const img = useFragment(
    graphql`
      fragment ImagePublic_image on PublicImage {
        publicImageUrl
      }
    `,
    image
  );

  return (
    <I
      {...props}
      responsivePolicy={props.responsivePolicy ?? "initial"}
      recyclingKey={img.publicImageUrl}
      transition={props.transition ?? defaultTransition}
      source={{
        uri: img.publicImageUrl,
      }}
    />
  );
}

export const RemoteImage = React.memo(RemoteImageC);
function RemoteImageC(_props: RemoteImageProps) {
  const { uploadedImages, image: imageKey, ...props } = _props;
  const image = useFragment(
    graphql`
      fragment Image_image on Image {
        id
        thumbhash
        sizes {
          id
          url
          urlExpiresAt
          height
          width
        }
      }
    `,
    imageKey
  );

  useSubscriptionWithSkip<ImageFinishUploadSubscription>(
    {
      variables: {
        id: image?.id,
      },
      subscription: graphql`
        subscription ImageFinishUploadSubscription($id: ID!) {
          imageFinishedUploading(imageIds: [$id]) {
            id
            ...Image_image
          }
        }
      `,
    },
    {
      skip: !image?.id || (image?.sizes?.length ?? 0) > 0,
    }
  );

  const relay = useRelayEnvironment();
  const [isRefetching, setIsRefetching] = useState(false);

  const refetchCount = useRef(0);

  const imageId = image?.id;

  useEffect(() => {
    refetchCount.current = 0;
  }, [imageId]);

  const refetchQuery = useCallback(async () => {
    if (isRefetching || refetchCount.current > 10) {
      return;
    }

    await new Promise((resolve) =>
      setTimeout(resolve, 500 * 2 ** refetchCount.current)
    );
    refetchCount.current++;
    setIsRefetching(true);
    fetchQuery<ImageRefetchQuery>(
      relay,
      graphql`
        query ImageRefetchQuery($id: ID!) {
          image(id: $id) {
            id
            ...Image_image
          }
        }
      `,
      {
        id: imageId,
      },
      {
        fetchPolicy: "network-only",
      }
    )
      .toPromise()
      .finally(() => {
        setIsRefetching(false);
      });
  }, [imageId, isRefetching, relay]);

  const sources =
    image?.sizes
      ?.filter((size) => size)
      .map(
        (size): ImageSource => ({
          uri: size.url,
          width: size.width / (Platform.OS === "web" ? 2 : 1),
          height: size.height / (Platform.OS === "web" ? 2 : 1),
          cacheKey: size.id,
        })
      ) ?? [];

  const source = () => {
    if (sources.length > 0) {
      return sources;
    }
    if (!uploadedImages) {
      return [];
    }

    const pendingUploadImage = uploadedImages.find(
      (img) => img.id === image?.id
    );

    if (pendingUploadImage) {
      return {
        uri:
          pendingUploadImage.status === "uploading" ||
          pendingUploadImage.status === "done"
            ? pendingUploadImage.localImageURI
            : pendingUploadImage.localImageURIOriginal,
      };
    }

    return [];
  };

  return (
    <I
      {...props}
      responsivePolicy={props.responsivePolicy ?? "initial"}
      onError={(e) => {
        refetchQuery();
        props.onError?.(e);
      }}
      transition={props.transition ?? defaultTransition}
      source={source()}
      placeholder={
        image?.thumbhash
          ? {
              thumbhash: image?.thumbhash,
            }
          : undefined
      }
    />
  );
}

type LocalImageSource = {
  type: "local";
  uri: string;
};
type LocalImageProps = Omit<IP, "source"> & LocalImageSource;

export type ImageProps =
  | (RemoteImageProps & { type: "remote" })
  | LocalImageProps;

export type SPImageSource =
  | (RemoteImageSource & { type: "remote" })
  | LocalImageSource;

export function Image(props: ImageProps) {
  const uploadedImages = useUploadedImages();

  if (props.type === "local") {
    return (
      <I
        {...props}
        source={{
          uri: props.uri,
        }}
      />
    );
  }

  return (
    <RemoteImage
      {...props}
      responsivePolicy="live"
      uploadedImages={uploadedImages}
    />
  );
}

export function PublicOrPrivateImage(
  _props: Omit<RemoteImageProps, "image"> & {
    image: ImagePublicOrPrivate_image$key;
  }
) {
  const { image, ...props } = _props;
  const img = useFragment(
    graphql`
      fragment ImagePublicOrPrivate_image on PublicAndInternalImage {
        ... on PublicImage {
          ...ImagePublic_image
          __typename
        }
        ... on Image {
          id
          ...Image_image
          __typename
        }
      }
    `,
    image
  );

  switch (img.__typename) {
    case "Image":
      return <RemoteImage {...props} image={img} />;
    case "PublicImage":
      return <RemotePublicImage {...props} image={img} />;
    default:
      return null;
  }
}
